import CustomMetaData, {
  CustomMetaDataProps,
} from 'components/general/customMetaData/CustomMetaData';
import { gsap } from 'gsap/dist/gsap';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
import { NEXTJS_REVALIDATE_PAGE_IN_SECONDS } from 'pages/index';
import { getSharedData } from 'utils/parsers';
import style from './indexNewsItems.module.scss';

import { SharedDataType, useGlobalContentContext } from 'context/GlobalContentContext';
import { NewsItemType } from './[slug]';
import { useEffect, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import {
  FeaturedNewsItem,
  FeaturedNewsItemType,
} from 'components/featurednewsItem/FeaturedNewsItem';
import { NewsItemCard } from 'components/newsItemCard/NewsItemCard';
import {
  NewsCategoryList,
  NewsItemCategoryType,
} from 'components/newsCategoryList/NewsCategoryList';
import { MainFooter } from 'components/mainFooter/MainFooter';
import { formatDate, getEndpoint, isMobileWidth } from 'utils/helpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { useScrollDirection } from 'hooks/useScroll';

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin);
}

type Props = {
  globalData: SharedDataType;
  featuredItem: FeaturedNewsItemType;
  newsItems: NewsItemType[];
  categories: NewsItemCategoryType[];
} & {
  metaData: CustomMetaDataProps;
};

const News = ({ newsItems, featuredItem, metaData, globalData, categories }: Props) => {
  const { setData } = useGlobalContentContext();
  const windowSize = useWindowSize();
  useEffect(() => {
    const copy = { ...globalData };
    if (isMobileWidth(windowSize.width)) {
      copy.sidebar.title = '';
    }
    setData(copy);
  }, [windowSize]);

  const scroll = useScrollDirection();

  const stickyState =
    scroll.direction === 'up' &&
    scroll.scrollY > 100 &&
    !document.body.classList.contains('js-no-mobile-sticky-menu')
      ? style.newsMenuStickLower
      : null;

  // we add this manually, so we know it's the ALL filter.
  const everythingFilter = categories[0];
  const [currentCategory, setCurrentCategory] = useState<NewsItemCategoryType>(everythingFilter);

  const onCategoryItemClick = (cat: NewsItemCategoryType) => {
    document.body.classList.add('js-no-mobile-sticky-menu');
    gsap.to(window, {
      duration: 1,
      scrollTo: { y: '#newsBlocks', offsetY: 80 },
      onComplete: () => {
        gsap.delayedCall(0.1, () => {
          document.body.classList.remove('js-no-mobile-sticky-menu');
        });
      },
    });

    setCurrentCategory(cat);
  };

  const filtered = newsItems.filter((project) => {
    return currentCategory.url === 'all-the-categories'
      ? !project.is_feature
      : project.category.url === currentCategory.url && !project.is_feature;
  });

  return (
    <div className={style.container}>
      <CustomMetaData {...metaData} />

      <div className={style.content}>
        <div className={style.grid}>
          <div className={style.featured}>{<FeaturedNewsItem {...featuredItem} />}</div>
          <div className={style.newsCategories}>
            <div className={`${style.newsMenu} ${stickyState}`}>
              <NewsCategoryList
                categories={categories}
                currentCategory={currentCategory}
                onCategorySelect={onCategoryItemClick}
              />
            </div>
            <div id="newsBlocks" className={style.newsBlocks}>
              <SwitchTransition>
                <CSSTransition key={currentCategory.url} in={true} timeout={500} classNames="fade">
                  <div className={`${style.newsGrid} `}>
                    {filtered.map((item) => {
                      return (
                        <div key={item.slug} className={style.newsGridItem}>
                          <NewsItemCard {...item} />
                        </div>
                      );
                    })}
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </div>
          </div>
        </div>
        <div className={`${style.footer} `}>
          <MainFooter showMenu={false} showSocial={true} />
        </div>
      </div>
    </div>
  );
};

export default News;

// This also gets called at build time
export async function getStaticProps(context) {
  const categories = await getEndpoint('/news-categories/', context);
  const articles = await getEndpoint('/news-articles/', context);
  const page = await getEndpoint('/news-index-page/', context);

  articles.forEach((article) => {
    article.date = formatDate(article.pub_time);
  });

  const featuredItem =
    articles.find((article) => {
      return article.is_feature;
    }) || null;

  const globalData = await getSharedData(context);
  globalData.sidebar = { title: page.sidebar_heading };

  const props: Props = {
    categories: [{ name: 'All', slug: 'all', url: 'all-the-categories' }, ...categories],
    newsItems: articles,
    globalData,
    metaData: {
      title: page.meta_title,
      description: page.meta_description,
      image: page.meta_image,
    },
    featuredItem: featuredItem,
  };
  return {
    props: props,
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS, // In seconds
  };
}
